<template>
  <div v-if="chartData.data">
    <ChartTitle>
      {{ title }}
    </ChartTitle>
    <FilterList v-if="selectedFilters.length">
      <FilterListItem v-for="filter in selectedFilters" :key="filter" :style="{ color: getColor(filter) }">
        {{ filter }}
      </FilterListItem>
    </FilterList>
    <ChartContainer>
      <TreeMapChart
        :data="chartData.data"
        :valueField="valueField"
        :categoryField="categoryField"
        :regionField="regionField"
        :isTotal="isTotal"
        :color="getColor"
      />
    </ChartContainer>
  </div>
</template>

<script>
import { prop, filter, pathOr, uniqBy } from 'ramda';
import { scaleLinear } from 'd3-scale';
import { interpolateHcl } from 'd3-interpolate';

import TreeMapChart from '../charts/treeMapChart/treeMapChart.component';
import { ChartContainer, ChartTitle, FilterList, FilterListItem } from '../charts/treeMapChart/treeMapChart.styles';
import { mapGetters } from 'vuex';
import { PREFIX as REGIONS_STORE } from '../../../modules/regions';
import { GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER } from '@/modules/regions/regions.getters';
import { getRegionField, getValueField, getCategoryField, parseNumber } from '../../utils/helpers';
import { YEAR_FIELD } from '../../constants';

export default {
  props: {
    data: { type: Array, default: () => [] },
    theme: { type: Object, default: () => {} },
  },
  components: { TreeMapChart, ChartContainer, ChartTitle, FilterList, FilterListItem },
  computed: {
    ...mapGetters(REGIONS_STORE, [GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER]),
    valueField() {
      return getValueField(this[GET_SELECTED_SUBTOPIC]);
    },
    regionField() {
      return getRegionField(this[GET_SELECTED_SUBTOPIC]);
    },
    categoryField() {
      return getCategoryField(this[GET_SELECTED_SUBTOPIC]);
    },
    selectedFilters() {
      return this[GET_SELECTED_FILTERS];
    },
    isTotal() {
      const selectedFilters = this.selectedFilters;
      return !selectedFilters.length;
    },
    title() {
      const subtopic = pathOr('', ['meta', 'customData', 'subtopic'], this[GET_SELECTED_SUBTOPIC]);
      const categoryString = this.categoryField && this.categoryField.length ? ' By ' + this.categoryField : '';
      return subtopic + categoryString;
    },
    chartData() {
      const valueField = this.valueField;
      const regionField = this.regionField;
      const categoryField = this.categoryField;

      let filteredData = [];
      if (this.isTotal) {
        filteredData = filter(d => {
          return d[YEAR_FIELD] === this[GET_YEAR_FILTER];
        }, this.data);
      } else {
        filteredData = filter(d => {
          return d[YEAR_FIELD] === this[GET_YEAR_FILTER] && this.selectedFilters.indexOf(d[regionField]) > -1;
        }, this.data);
      }

      const groupField = categoryField || regionField;

      // Add id and parent
      filteredData.forEach(d => {
        d.id = d[YEAR_FIELD] + d[regionField] + d[groupField] + d[valueField];
        d.parent = groupField ? d[groupField] : 'root';
        d[valueField] = parseNumber(d[valueField]);
      });

      // Group into categories
      if (groupField) {
        const categoryData = uniqBy(prop(groupField), filteredData).map(d => {
          return {
            id: d[groupField],
            parent: 'root',
            [groupField]: d[groupField],
          };
        });

        // console.log('categoryData', categoryData);

        // Insert top level category nodes
        filteredData = categoryData.concat(filteredData);
      }

      // Insert root item
      filteredData.unshift({
        id: 'root',
        [YEAR_FIELD]: this[GET_YEAR_FILTER],
        [regionField]: 'All',
        parent: '',
      });

      // console.log('filteredData', filteredData);

      return {
        data: filteredData,
      };
    },
    colorScale() {
      const max = this.selectedFilters.length;

      return scaleLinear()
        .domain([0, max])
        .range([this.theme.mainColor, this.theme.lightColor])
        .interpolate(interpolateHcl);
    },
  },
  methods: {
    getColor(filter) {
      const max = this.selectedFilters.length;
      const index = this.selectedFilters.indexOf(filter);

      return max > 0 ? this.colorScale(index) : this.theme.mainColor;
    },
  },
};
</script>
